import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { resetALL } from 'Redux/actions';
import { ExceptionManager } from 'Core/logManager';
import { clientQuery, clientMutation } from 'Core/data/GraphQLApi';
import { endpoints } from 'Core/defaultValues';

const route = 'modules/account/views/settings/redux/slice';

// Default settings matching the GraphQL schema
const DEFAULT_SETTINGS = {
  defaultZoneLayoutId: '',
  imageRetention: -1,
  inspectionRetention: -1,
};

const INIT_STATE = {
  loading: false,
  saving: false,
  saveSuccess: false,
  error: null,
  zoneLayouts: [],
  zoneLayoutsLoading: false,
  settings: DEFAULT_SETTINGS,
  originalSettings: DEFAULT_SETTINGS,
  settingsExist: false,
};

export const fetchZoneLayouts = createAsyncThunk(
  'account/settings/fetchZoneLayouts',
  async (_, { rejectWithValue }) => {
    try {
      const data = await clientQuery(
        `
          query searchInspectionLayouts($limit: Int) {
            searchInspectionLayouts(limit: $limit) {
              items {
                id
                name
              }
              total
            }
          }
        `,
        { limit: 100 },
        endpoints.GRAPHQL_GENERAL_V2,
      );

      if (data?.searchInspectionLayouts?.items) {
        return data.searchInspectionLayouts.items;
      }
      return [];
    } catch (exc) {
      ExceptionManager(exc, route, 'fetchZoneLayouts');
      return rejectWithValue(exc.message || 'Failed to load zone layouts');
    }
  },
);

export const fetchSettings = createAsyncThunk(
  'account/settings/fetchSettings',
  async (_, { rejectWithValue }) => {
    try {
      const data = await clientQuery(
        `
          query getEvirSetting {
            getEvirSetting {
              data {
                defaultZoneLayoutId
                imageRetention
                inspectionRetention
              }
              changelog
              createdOn
              updatedOn
            }
          }
        `,
        {},
        endpoints.GRAPHQL_GENERAL_V2,
      );

      if (data?.getEvirSetting?.data) {
        const loadedData = data.getEvirSetting.data;
        return {
          exists: true,
          settings: {
            defaultZoneLayoutId: loadedData.defaultZoneLayoutId || '',
            imageRetention: loadedData.imageRetention ?? -1,
            inspectionRetention: loadedData.inspectionRetention ?? -1,
          },
        };
      }
      return { exists: false, settings: DEFAULT_SETTINGS };
    } catch (exc) {
      ExceptionManager(exc, route, 'fetchSettings');
      return rejectWithValue(exc.message || 'Failed to load settings');
    }
  },
);

export const saveSettings = createAsyncThunk(
  'account/settings/saveSettings',
  async ({ settings, settingsExist, userName }, { rejectWithValue }) => {
    try {
      const dataInput = {
        imageRetention: parseInt(settings.imageRetention, 10),
        inspectionRetention: parseInt(settings.inspectionRetention, 10),
      };

      if (settings.defaultZoneLayoutId) {
        dataInput.defaultZoneLayoutId = settings.defaultZoneLayoutId;
      }

      const changelog = JSON.stringify({
        timestamp: new Date().toISOString(),
        changedBy: userName,
      });

      let response;

      if (settingsExist) {
        response = await clientMutation(
          `
            mutation updateEvirSetting($data: UpdateEvirSettingInput!, $changelog: AWSJSON) {
              updateEvirSetting(data: $data, changelog: $changelog) {
                data {
                  defaultZoneLayoutId
                  imageRetention
                  inspectionRetention
                }
                changelog
              }
            }
          `,
          { data: dataInput, changelog },
          endpoints.GRAPHQL_GENERAL_V2,
        );

        if (response?.updateEvirSetting) {
          return { success: true, settings };
        }
      } else {
        if (!settings.defaultZoneLayoutId) {
          return rejectWithValue('Default zone layout is required for initial setup');
        }

        const createInput = {
          defaultZoneLayoutId: settings.defaultZoneLayoutId,
          imageRetention: parseInt(settings.imageRetention, 10),
          inspectionRetention: parseInt(settings.inspectionRetention, 10),
        };

        response = await clientMutation(
          `
            mutation createEvirSetting($data: CreateEvirSettingInput!, $changelog: AWSJSON) {
              createEvirSetting(data: $data, changelog: $changelog) {
                data {
                  defaultZoneLayoutId
                  imageRetention
                  inspectionRetention
                }
                changelog
              }
            }
          `,
          { data: createInput, changelog },
          endpoints.GRAPHQL_GENERAL_V2,
        );

        if (response?.createEvirSetting) {
          return { success: true, settings, created: true };
        }
      }

      return rejectWithValue('Save failed: No response data');
    } catch (exc) {
      ExceptionManager(exc, route, 'saveSettings');
      return rejectWithValue(exc.message || 'Failed to save settings');
    }
  },
);

export const AccountSettingsSlice = createSlice({
  name: 'account/settings',
  initialState: INIT_STATE,
  reducers: {
    resetSettings: (state, action) => {
      return action.payload ? { ...state, ...action.payload } : { ...INIT_STATE };
    },
    updateLocalSettings: (state, action) => {
      state.settings = { ...state.settings, ...action.payload };
    },
    revertSettings: (state) => {
      state.settings = state.originalSettings;
    },
    clearSaveSuccess: (state) => {
      state.saveSuccess = false;
    },
  },
  extraReducers: (builder) => {
    // Fetch zone layouts
    builder.addCase(fetchZoneLayouts.pending, (state) => {
      state.zoneLayoutsLoading = true;
    });
    builder.addCase(fetchZoneLayouts.fulfilled, (state, { payload }) => {
      state.zoneLayoutsLoading = false;
      state.zoneLayouts = payload;
    });
    builder.addCase(fetchZoneLayouts.rejected, (state, { payload }) => {
      state.zoneLayoutsLoading = false;
      state.error = payload;
    });

    // Fetch settings
    builder.addCase(fetchSettings.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchSettings.fulfilled, (state, { payload }) => {
      state.loading = false;
      state.settingsExist = payload.exists;
      state.settings = payload.settings;
      state.originalSettings = payload.settings;
    });
    builder.addCase(fetchSettings.rejected, (state, { payload }) => {
      state.loading = false;
      state.error = payload;
      state.settingsExist = false;
    });

    // Save settings
    builder.addCase(saveSettings.pending, (state) => {
      state.saving = true;
      state.saveSuccess = false;
      state.error = null;
    });
    builder.addCase(saveSettings.fulfilled, (state, { payload }) => {
      state.saving = false;
      state.saveSuccess = true;
      state.settingsExist = true;
      state.originalSettings = payload.settings;
    });
    builder.addCase(saveSettings.rejected, (state, { payload }) => {
      state.saving = false;
      state.saveSuccess = false;
      state.error = payload;
    });

    // Global reset
    builder.addCase(resetALL, () => INIT_STATE);
  },
});

const { actions, reducer } = AccountSettingsSlice;

export const { resetSettings, updateLocalSettings, revertSettings, clearSaveSuccess } = actions;

export default reducer;
